// src/components/dashboard/BackupMonitor.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Activity, AlertTriangle, CheckCircle, XCircle, Clock, 
  TrendingUp, Bell, BellOff, RefreshCw, Database, HardDrive, 
  Wifi, WifiOff, Settings, Eye, Download
} from 'lucide-react';

import { useBackup } from '../../context/BackupContext';
import BackupService from '../../services/BackupService';
import { 
  formatFileSize, 
  formatRelativeTime, 
  calculateBackupStats 
} from '../../services/backupUtils';
import { BACKUP_CONFIG } from '../../services/backupConfig';

const BackupMonitor = () => {
  const { user } = useBackup();
  const backupService = new BackupService();
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [realtimeData, setRealtimeData] = useState({
    activeBackups: 0,
    queuedBackups: 0,
    storageUsed: 0,
    lastActivity: null
  });
  const [alerts, setAlerts] = useState([]);
  const [systemHealth, setSystemHealth] = useState({
    database: { isHealthy: false, lastCheck: null },
    storage: { isHealthy: false, lastCheck: null },
    overall: { isHealthy: false }
  });
  const [settings, setSettings] = useState({
    refreshInterval: 30000,
    enableAlerts: true,
    alertThresholds: {
      storageUsage: 80,
      failedBackups: 3,
      oldestBackup: 7
    }
  });
  const [stats, setStats] = useState({
    totalBackups: 0,
    successRate: 100,
    averageSize: 0,
    lastBackup: null
  });

  const intervalRef = useRef(null);

  // Démarrer/arrêter le monitoring
  const toggleMonitoring = () => {
    if (isMonitoring) {
      stopMonitoring();
    } else {
      startMonitoring();
    }
  };

  const startMonitoring = () => {
    setIsMonitoring(true);
    loadAllData();
    
    // Interval pour les updates automatiques
    intervalRef.current = setInterval(loadAllData, settings.refreshInterval);
  };

  const stopMonitoring = () => {
    setIsMonitoring(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Charger toutes les données
  const loadAllData = async () => {
    await Promise.all([
      loadRealtimeData(),
      loadSystemHealth(),
      loadAlerts(),
      loadStats()
    ]);
  };

  // Données temps réel
  const loadRealtimeData = async () => {
    try {
      const backups = await backupService.listBackups({ limit: 100 });
      const storage = await backupService.getStorageUsage();
      
      const activeBackups = backups.filter(b => b.status === 'in_progress').length;
      const queuedBackups = 0; // Pas de système de queue implémenté
      
      setRealtimeData({
        activeBackups,
        queuedBackups,
        storageUsed: storage.totalSize || 0,
        lastActivity: backups.length > 0 ? Math.max(...backups.map(b => b.timestamp)) : null
      });
    } catch (err) {
      console.error('Erreur chargement données temps réel:', err);
    }
  };

  // Vérifier la santé du système
  const loadSystemHealth = async () => {
    const now = Date.now();
    
    // Test base de données
    const dbHealth = await checkDatabaseHealth();
    
    // Test stockage
    const storageHealth = await checkStorageHealth();
    
    // État global
    const overallHealth = {
      isHealthy: dbHealth.isHealthy && storageHealth.isHealthy
    };

    setSystemHealth({
      database: { ...dbHealth, lastCheck: now },
      storage: { ...storageHealth, lastCheck: now },
      overall: overallHealth
    });
  };

  const checkDatabaseHealth = async () => {
    try {
      await backupService.listBackups({ limit: 1 });
      return { isHealthy: true };
    } catch (error) {
      return { 
        isHealthy: false, 
        error: `Base de données: ${error.message}` 
      };
    }
  };

  const checkStorageHealth = async () => {
    try {
      await backupService.getStorageUsage();
      return { isHealthy: true };
    } catch (error) {
      return { 
        isHealthy: false, 
        error: `Stockage: ${error.message}` 
      };
    }
  };

  // Charger les alertes
  const loadAlerts = async () => {
    try {
      const backups = await backupService.listBackups({ limit: 50 });
      const storage = await backupService.getStorageUsage();
      const newAlerts = [];

      // Alerte: Sauvegardes échouées récentes
      const recentFailed = backups.filter(b => 
        b.status === 'failed' && 
        (Date.now() - b.timestamp) < 24 * 60 * 60 * 1000
      );
      
      if (recentFailed.length >= settings.alertThresholds.failedBackups) {
        newAlerts.push({
          id: 'failed_backups',
          type: 'error',
          title: 'Sauvegardes échouées',
          message: `${recentFailed.length} sauvegardes ont échoué dans les dernières 24h`,
          timestamp: Date.now()
        });
      }

      // Alerte: Aucune sauvegarde récente
      const lastBackup = backups.find(b => b.status === 'completed');
      if (!lastBackup || 
          (Date.now() - lastBackup.timestamp) > settings.alertThresholds.oldestBackup * 24 * 60 * 60 * 1000) {
        newAlerts.push({
          id: 'no_recent_backup',
          type: 'warning',
          title: 'Sauvegarde ancienne',
          message: `Aucune sauvegarde réussie depuis ${settings.alertThresholds.oldestBackup} jours`,
          timestamp: Date.now()
        });
      }

      // Alerte: Stockage élevé
      const storageUsagePercent = storage.totalSize ? 
        (storage.totalSize / (100 * 1024 * 1024)) * 100 : 0; // Pourcentage approximatif
      
      if (storageUsagePercent > settings.alertThresholds.storageUsage) {
        newAlerts.push({
          id: 'high_storage',
          type: 'warning',
          title: 'Utilisation stockage élevée',
          message: `Stockage utilisé: ${formatFileSize(storage.totalSize)}`,
          timestamp: Date.now()
        });
      }

      // Alerte: Aucune sauvegarde
      if (backups.length === 0) {
        newAlerts.push({
          id: 'no_backups',
          type: 'error',
          title: 'Aucune sauvegarde',
          message: 'Aucune sauvegarde trouvée dans le système',
          timestamp: Date.now()
        });
      }

      setAlerts(newAlerts);
    } catch (err) {
      console.error('Erreur chargement alertes:', err);
    }
  };

  // Charger les statistiques
  const loadStats = async () => {
    try {
      const backups = await backupService.listBackups({ limit: 100 });
      const backupStats = calculateBackupStats(backups);
      
      setStats({
        totalBackups: backupStats.total,
        successRate: backupStats.successRate,
        averageSize: backupStats.averageSize,
        lastBackup: backups.find(b => b.status === 'completed')?.timestamp || null
      });
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  };

  // Actualisation manuelle
  const handleRefresh = () => {
    loadAllData();
  };

  // Effets
  useEffect(() => {
    loadAllData();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Rendu des icônes de statut
  const getHealthIcon = (isHealthy) => {
    return isHealthy ? (
      <CheckCircle className="w-4 h-4 text-green-500" />
    ) : (
      <XCircle className="w-4 h-4 text-red-500" />
    );
  };

  const getHealthStatus = (isHealthy) => {
    return isHealthy ? 'healthy' : 'warning';
  };

  // Interface du composant
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Activity className="w-6 h-6 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Monitoring des Sauvegardes
                </h1>
                <p className="text-gray-600">
                  {isMonitoring ? (
                    <span className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      <span>En cours</span>
                    </span>
                  ) : (
                    <span className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                      <span>Arrêté</span>
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleMonitoring}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isMonitoring
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {isMonitoring ? 'Arrêter' : 'Démarrer'}
              </button>
              <button
                onClick={handleRefresh}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Actualiser"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation des vues */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex border-b border-gray-200">
            {[
              { id: 'dashboard', label: 'Tableau de bord', icon: Activity },
              { id: 'alerts', label: 'Alertes', icon: AlertTriangle },
              { id: 'stats', label: 'Statistiques', icon: TrendingUp },
              { id: 'settings', label: 'Paramètres', icon: Settings }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setCurrentView(id)}
                className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors ${
                  currentView === id
                    ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
                {id === 'alerts' && alerts.length > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {alerts.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Contenu des vues */}
        {currentView === 'dashboard' && (
          <div className="space-y-6">
            {/* Métriques temps réel */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Sauvegardes actives</p>
                    <p className="text-2xl font-bold text-gray-900">{realtimeData.activeBackups}</p>
                  </div>
                  <Activity className="w-8 h-8 text-blue-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">File d'attente</p>
                    <p className="text-2xl font-bold text-gray-900">{realtimeData.queuedBackups}</p>
                  </div>
                  <Clock className="w-8 h-8 text-orange-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Stockage utilisé</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatFileSize(realtimeData.storageUsed)}
                    </p>
                  </div>
                  <HardDrive className="w-8 h-8 text-purple-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Dernière activité</p>
                    <p className="text-sm text-gray-900">
                      {realtimeData.lastActivity ? 
                        formatRelativeTime(realtimeData.lastActivity) : 
                        'Aucune'
                      }
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-green-500" />
                </div>
              </div>
            </div>

            {/* État de santé du système */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                État de Santé du Système
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Database className="w-6 h-6 text-blue-500" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Base de données</p>
                    <p className="text-sm text-gray-600">
                      {getHealthStatus(systemHealth.database.isHealthy)}
                    </p>
                  </div>
                  {getHealthIcon(systemHealth.database.isHealthy)}
                  <span className="text-xs text-gray-500">
                    {systemHealth.database.lastCheck ? 
                      formatRelativeTime(systemHealth.database.lastCheck) : 
                      'Non testé'
                    }
                  </span>
                </div>

                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <HardDrive className="w-6 h-6 text-purple-500" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Stockage</p>
                    <p className="text-sm text-gray-600">
                      {getHealthStatus(systemHealth.storage.isHealthy)}
                    </p>
                  </div>
                  {getHealthIcon(systemHealth.storage.isHealthy)}
                  <span className="text-xs text-gray-500">
                    {systemHealth.storage.lastCheck ? 
                      formatRelativeTime(systemHealth.storage.lastCheck) : 
                      'Non testé'
                    }
                  </span>
                </div>

                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Wifi className="w-6 h-6 text-green-500" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">État global</p>
                    <p className="text-sm text-gray-600">
                      {getHealthStatus(systemHealth.overall.isHealthy)}
                    </p>
                  </div>
                  {getHealthIcon(systemHealth.overall.isHealthy)}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentView === 'alerts' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Alertes du Système
              </h3>
              <div className="flex items-center space-x-2">
                {settings.enableAlerts ? (
                  <Bell className="w-5 h-5 text-green-500" />
                ) : (
                  <BellOff className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-sm text-gray-600">
                  {alerts.length} alerte(s)
                </span>
              </div>
            </div>

            {alerts.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-gray-600">Aucune alerte active</p>
                <p className="text-sm text-gray-500">Tous les systèmes fonctionnent normalement</p>
              </div>
            ) : (
              <div className="space-y-4">
                {alerts.map(alert => (
                  <div
                    key={alert.id}
                    className={`p-4 rounded-lg border-l-4 ${
                      alert.type === 'error'
                        ? 'bg-red-50 border-red-500'
                        : alert.type === 'warning'
                        ? 'bg-yellow-50 border-yellow-500'
                        : 'bg-blue-50 border-blue-500'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {alert.type === 'error' ? (
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                      ) : alert.type === 'warning' ? (
                        <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{alert.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {formatRelativeTime(alert.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {currentView === 'stats' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Statistiques des Sauvegardes
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {stats.totalBackups}
                </div>
                <div className="text-sm font-medium text-blue-900">
                  Total Sauvegardes
                </div>
              </div>

              <div className="text-center p-6 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {stats.successRate.toFixed(1)}%
                </div>
                <div className="text-sm font-medium text-green-900">
                  Taux de Réussite
                </div>
              </div>

              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {formatFileSize(stats.averageSize)}
                </div>
                <div className="text-sm font-medium text-purple-900">
                  Taille Moyenne
                </div>
              </div>

              <div className="text-center p-6 bg-orange-50 rounded-lg">
                <div className="text-3xl font-bold text-orange-600 mb-2">
                  {stats.lastBackup ? formatRelativeTime(stats.lastBackup) : 'Jamais'}
                </div>
                <div className="text-sm font-medium text-orange-900">
                  Dernière Sauvegarde
                </div>
              </div>
            </div>
          </div>
        )}

        {currentView === 'settings' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Paramètres du Monitoring
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Intervalle d'actualisation (ms)
                </label>
                <input
                  type="number"
                  value={settings.refreshInterval}
                  onChange={(e) => setSettings({
                    ...settings,
                    refreshInterval: parseInt(e.target.value) || 30000
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="enableAlerts"
                  checked={settings.enableAlerts}
                  onChange={(e) => setSettings({
                    ...settings,
                    enableAlerts: e.target.checked
                  })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="enableAlerts" className="text-sm font-medium text-gray-700">
                  Activer les alertes
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seuil stockage (%)
                  </label>
                  <input
                    type="number"
                    value={settings.alertThresholds.storageUsage}
                    onChange={(e) => setSettings({
                      ...settings,
                      alertThresholds: {
                        ...settings.alertThresholds,
                        storageUsage: parseInt(e.target.value) || 80
                      }
                    })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Échecs consécutifs
                  </label>
                  <input
                    type="number"
                    value={settings.alertThresholds.failedBackups}
                    onChange={(e) => setSettings({
                      ...settings,
                      alertThresholds: {
                        ...settings.alertThresholds,
                        failedBackups: parseInt(e.target.value) || 3
                      }
                    })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Âge maximum (jours)
                  </label>
                  <input
                    type="number"
                    value={settings.alertThresholds.oldestBackup}
                    onChange={(e) => setSettings({
                      ...settings,
                      alertThresholds: {
                        ...settings.alertThresholds,
                        oldestBackup: parseInt(e.target.value) || 7
                      }
                    })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BackupMonitor;